<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';
import { useCardChat, type CardChatGrade } from '../composables/useCardChat';
import { MessageSquare } from 'lucide-vue-next';
import { marked } from 'marked';

marked.setOptions({ async: false });

function normalizeMarkdown(text: string): string {
  return text
    .replace(/(?<!\n)\n(#{1,6} )/g, '\n\n$1')
    .replace(/(?<!\n)\n(---+|\*\*\*+|___+)\s*$/gm, '\n\n$1')
    .replace(/([^\n])\n([-*] |\d+\. )/g, '$1\n\n$2');
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// The unified review card. (Backed by the flashcards table — `answer` is the model
// answer, always present, so reveal-and-self-grade is always available.)
interface Card {
  id: string;
  shortId: string;
  question: string;
  answer: string;
  goal: string | null;
  // Present in the global queue (cards span curricula); absent in curriculum-scoped
  // review, where the route supplies the curriculum. A card with no lesson has null.
  curriculumId?: string | null;
  curriculumTitle?: string | null;
}

type TranscriptEntry = { role: 'learner'; text: string };

// 'revealed' = the learner chose to see the model answer and self-grade instead of
// typing a response.
type ItemStatus = 'idle' | 'submitting' | 'graded' | 'revealed' | 'error';

type Rating = 1 | 2 | 3 | 4;

// gradeCard is one-shot (no probe): it always returns a graded outcome.
interface GradeOutcome {
  status: 'graded';
  rating?: Rating;
  notes?: string | null;
}

const ratingMeta: Record<Rating, { label: string; color: string }> = {
  1: { label: 'Again', color: 'rating-again' },
  2: { label: 'Hard', color: 'rating-hard' },
  3: { label: 'Good', color: 'rating-good' },
  4: { label: 'Easy', color: 'rating-easy' },
};

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const sessionLoading = computed(() => session.value?.isPending);

// No :id in the route → the global queue (all due cards across curricula); an :id →
// review scoped to that one curriculum. The two modes differ only in how loadQueue
// sources cards and in a few labels.
const curriculumId = computed(() =>
  typeof route.params.id === 'string' ? route.params.id : undefined
);
const isGlobal = computed(() => !curriculumId.value);

const dataLoading = ref(true);
const queue = ref<Card[]>([]);
const totalInSession = ref(0);
const currentIndex = ref(0);

const transcript = ref<TranscriptEntry[]>([]);
const inputText = ref('');
const itemStatus = ref<ItemStatus>('idle');
const errorMessage = ref('');
// `gradedRating` is what will be committed — it starts at the grader's proposal
// (`aiRating`) and the learner can override it via the grade buttons. Not written to
// FSRS until they advance (see `advance`).
const gradedRating = ref<Rating | null>(null);
const aiRating = ref<Rating | null>(null);
const gradedNotes = ref<string | null>(null);
const lastSubmittedResponse = ref('');
const committing = ref(false);

const ratingTotals = ref<Record<Rating, number>>({ 1: 0, 2: 0, 3: 0, 4: 0 });
const cardsReviewed = ref(0);

const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const chatSessionId = ref<string | null>(null);

// Post-grade reflection chat (carries the on-screen grade).
const {
  messages: chatMessages,
  isStreaming: chatStreaming,
  error: chatError,
  cardUpdate: reflectionCardUpdate,
  archived: reflectionArchived,
  advanceRequested,
  sendMessage: sendChatMessage,
  reset: resetChat,
} = useCardChat();

// Gradeless "discuss / edit this card" chat — the pre-grade tutor thread, rendered
// inline in the same conversation (no separate panel). Both threads feed `liveCardEdit`
// so the question/answer update live when the tutor edits the card.
const {
  messages: editChatMessages,
  isStreaming: editChatStreaming,
  error: editChatError,
  cardUpdate: editCardUpdate,
  archived: editArchived,
  sendMessage: sendEditChatMessage,
  reset: resetEditChat,
} = useCardChat();

// The "Discuss / edit this card" button reveals a row of quick actions (retire,
// reject, too easy/hard). While the row is open and the card isn't graded yet, the
// text input talks to the tutor (discuss mode) instead of submitting an answer.
const actionsOpen = ref(false);
const manualArchived = ref(false);
const archiving = ref(false);

// The card shows as a flashcard by default (prominent question, "Show answer",
// self-grade). Typing an answer to be graded is the opt-in path: once the learner
// chooses it, we switch to the conversational view. Reset per card.
const typedMode = ref(false);

// The displayed card reflects live edits from either chat thread; the watcher below
// writes here so the card updates live.
const liveCardEdit = ref<{ question: string; answer: string } | null>(null);
const isItemArchived = computed(
  () => reflectionArchived.value || editArchived.value || manualArchived.value
);
const discussMode = computed(
  () => actionsOpen.value && itemStatus.value !== 'graded' && !isItemArchived.value
);

// Reveal is offered before the learner has engaged (idle, not mid-discuss). Cards
// always carry a model answer, so it's always available.
const canReveal = computed(
  () => itemStatus.value === 'idle' && !discussMode.value && !isItemArchived.value
);

// Flashcard presentation: prominent question card + Show answer / self-grade. The
// default for a fresh card and after revealing. We fall back to the conversational
// view once the learner types an answer, opens discuss/edit, or has been graded.
const flashcardMode = computed(
  () =>
    (itemStatus.value === 'idle' || itemStatus.value === 'revealed') &&
    !typedMode.value &&
    !discussMode.value &&
    !isItemArchived.value
);

const currentItem = computed<Card | null>(() => queue.value[currentIndex.value] ?? null);

const displayItem = computed<Card | null>(() => {
  if (!currentItem.value) return null;
  return liveCardEdit.value ? { ...currentItem.value, ...liveCardEdit.value } : currentItem.value;
});

type ChatBubble =
  | { kind: 'prompt'; text: string; goal: string | null }
  | { kind: 'learner'; text: string }
  | { kind: 'answer'; text: string }
  | { kind: 'grade'; rating: Rating; notes: string | null }
  | { kind: 'thinking' }
  | {
      kind: 'chat';
      role: 'user' | 'assistant';
      content: string;
      isStreaming?: boolean;
      toolUses?: import('../composables/useCardChat').CardToolUse[];
    };

const chatLog = computed<ChatBubble[]>(() => {
  const bubbles: ChatBubble[] = [];
  if (!displayItem.value) return bubbles;

  bubbles.push({ kind: 'prompt', text: displayItem.value.question, goal: displayItem.value.goal });

  for (const entry of transcript.value) {
    bubbles.push({ kind: 'learner', text: entry.text });
  }

  if (itemStatus.value === 'submitting') {
    bubbles.push({ kind: 'thinking' });
  }

  if (itemStatus.value === 'revealed') {
    bubbles.push({ kind: 'answer', text: displayItem.value.answer });
  }

  if (itemStatus.value === 'graded' && gradedRating.value !== null) {
    bubbles.push({ kind: 'grade', rating: gradedRating.value, notes: gradedNotes.value });
  }

  // Pre-grade "discuss / edit" thread renders inline as part of the one conversation.
  for (const m of editChatMessages.value) {
    bubbles.push({ kind: 'chat', role: m.role, content: m.content, isStreaming: m.isStreaming, toolUses: m.toolUses });
  }

  for (const m of chatMessages.value) {
    bubbles.push({ kind: 'chat', role: m.role, content: m.content, isStreaming: m.isStreaming, toolUses: m.toolUses });
  }

  return bubbles;
});

const inputPlaceholder = computed(() => {
  if (isItemArchived.value) return 'This card has been archived.';
  if (discussMode.value) return 'Ask about this card, or describe a change…';
  if (itemStatus.value === 'graded') return 'Push back, ask a follow-up, or refine this card...';
  return 'Type your answer...';
});

const inputDisabled = computed(
  () =>
    itemStatus.value === 'submitting' ||
    chatStreaming.value ||
    editChatStreaming.value ||
    isItemArchived.value
);

const isSessionComplete = computed(() => {
  if (dataLoading.value) return false;
  if (totalInSession.value === 0) return false;
  return currentIndex.value >= queue.value.length;
});

const isQueueEmpty = computed(() => !dataLoading.value && totalInSession.value === 0);

function buildTranscriptString(): string | undefined {
  if (transcript.value.length === 0) return undefined;
  return transcript.value.map((entry) => `Learner: ${entry.text}`).join('\n\n');
}

function resetItemState() {
  transcript.value = [];
  inputText.value = '';
  itemStatus.value = 'idle';
  errorMessage.value = '';
  gradedRating.value = null;
  aiRating.value = null;
  gradedNotes.value = null;
  lastSubmittedResponse.value = '';
  committing.value = false;
  if (textareaRef.value) textareaRef.value.style.height = 'auto';
  // Drop the session so the next card opens its own chat (scoped to that card's
  // curriculum) rather than reusing the previous card's.
  chatSessionId.value = null;
  resetChat();
  // Reset per-card discuss/edit state so it doesn't bleed into the next card.
  actionsOpen.value = false;
  manualArchived.value = false;
  archiving.value = false;
  typedMode.value = false;
  liveCardEdit.value = null;
  resetEditChat();
}

// Lazy, per-card review session: created only when the learner actually discusses or
// reflects on a card (most reviews are pure self-grades and never need one). Reset
// between cards (see resetItemState), so each chat is scoped to its own card — which is
// what lets the global queue mix curricula: the session takes the current card's
// curriculum, falling back to the route's in curriculum-scoped review.
async function ensureChatSession(): Promise<string | null> {
  if (chatSessionId.value) return chatSessionId.value;
  try {
    const response = await fetch(`${API_URL}/api/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        type: 'review',
        curriculumId: currentItem.value?.curriculumId ?? curriculumId.value ?? undefined,
      }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = (await response.json()) as { id: string };
    chatSessionId.value = data.id;
    return data.id;
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to start review chat';
    return null;
  }
}

// The grade isn't committed until the learner advances, so the reflection chat can't
// read it from a review log — we hand it the on-screen grade directly ("context ==
// screen"), including the grader's original pick so the tutor knows of any override.
function buildChatGrade(): CardChatGrade | undefined {
  if (itemStatus.value !== 'graded' || gradedRating.value === null) return undefined;
  return {
    rating: gradedRating.value,
    aiRating: aiRating.value ?? undefined,
    learnerResponse: lastSubmittedResponse.value || undefined,
    graderNotes: gradedNotes.value,
  };
}

async function handlePostGradeSubmit(text: string) {
  if (!currentItem.value || chatStreaming.value || isItemArchived.value) return;
  const sid = await ensureChatSession();
  if (!sid) return;
  scrollToBottom();
  await sendChatMessage(sid, currentItem.value.id, text, buildChatGrade());
  scrollToBottom();
}

async function triggerTutorOpening() {
  if (!currentItem.value || chatStreaming.value || isItemArchived.value) return;
  const sid = await ensureChatSession();
  if (!sid) return;
  scrollToBottom();
  await sendChatMessage(sid, currentItem.value.id, undefined, buildChatGrade());
  scrollToBottom();
}

// Toggle the quick-action row. Pre-grade, opening it puts the input in discuss mode
// (see `discussMode`); closing it returns to answering.
function toggleActions() {
  actionsOpen.value = !actionsOpen.value;
}

// Send a message into whichever thread is active: the reflection chat once graded,
// otherwise the gradeless discuss thread. Used by the Too easy / Too hard buttons.
async function sendItemMessage(text: string) {
  if (!currentItem.value || isItemArchived.value) return;
  const sid = await ensureChatSession();
  if (!sid) return;
  scrollToBottom();
  if (itemStatus.value === 'graded') {
    if (chatStreaming.value) return;
    await sendChatMessage(sid, currentItem.value.id, text, buildChatGrade());
  } else {
    if (editChatStreaming.value) return;
    actionsOpen.value = true;
    await sendEditChatMessage(sid, currentItem.value.id, text);
  }
  scrollToBottom();
}

// Archive the card with intent: 'retire' (done with a fine card) vs 'reject' (bad
// card — a quality signal). Disables the input; the existing Skip/Next advances.
async function archiveItem(reason: 'retire' | 'reject') {
  if (!currentItem.value || archiving.value || isItemArchived.value) return;
  archiving.value = true;
  errorMessage.value = '';
  try {
    await trpc.archiveCard.mutate({ idOrShortId: currentItem.value.id, reason });
    manualArchived.value = true;
    actionsOpen.value = false;
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to archive card';
  } finally {
    archiving.value = false;
  }
}

function toolLabel(name: string, status: string): string {
  const running = status === 'running';
  switch (name) {
    case 'update_card':
      return running ? 'Updating card…' : 'Updated card';
    case 'archive_card':
      return running ? 'Archiving card…' : 'Archived card';
    case 'advance_to_next_item':
      return running ? 'Moving on…' : 'Moving on';
    case 'list_curriculum_cards':
      return running ? 'Checking cards…' : 'Checked cards';
    case 'create_card':
      return running ? 'Creating card…' : 'Created card';
    default:
      return running ? 'Working…' : 'Done';
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function autoResize(event: Event) {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
  scrollToBottom();
}

async function loadQueue() {
  dataLoading.value = true;
  errorMessage.value = '';
  try {
    const cards: Card[] = isGlobal.value
      ? await trpc.listDueCards.query({ limit: 20 })
      : ((await trpc.listFlashcards.query({
          curriculumId: curriculumId.value,
          dueOnly: true,
          limit: 10,
        })) as Card[]);
    queue.value = cards;
    totalInSession.value = cards.length;
    currentIndex.value = 0;
    cardsReviewed.value = 0;
    ratingTotals.value = { 1: 0, 2: 0, 3: 0, 4: 0 };
    resetItemState();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to load review queue';
  } finally {
    dataLoading.value = false;
  }
}

async function gradeCurrent(learnerResponse: string) {
  if (!currentItem.value) return;

  itemStatus.value = 'submitting';
  errorMessage.value = '';
  lastSubmittedResponse.value = learnerResponse;

  try {
    const outcome = (await trpc.gradeCard.mutate({
      idOrShortId: currentItem.value.id,
      learnerResponse,
      transcript: buildTranscriptString(),
    })) as GradeOutcome;

    if (outcome.status === 'graded' && outcome.rating) {
      // Propose the grade but don't commit yet — the learner can override it with the
      // grade buttons, and `advance` writes the final rating to FSRS.
      gradedRating.value = outcome.rating;
      aiRating.value = outcome.rating;
      gradedNotes.value = outcome.notes ?? null;
      itemStatus.value = 'graded';
      scrollToBottom();
      void triggerTutorOpening();
      return;
    }

    throw new Error('Unexpected grader response');
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to grade response';
    itemStatus.value = 'error';
  }
}

async function handleSubmit() {
  const text = inputText.value.trim();
  if (!text) return;
  if (itemStatus.value === 'submitting' || chatStreaming.value || editChatStreaming.value) return;
  if (isItemArchived.value) return;
  inputText.value = '';
  if (textareaRef.value) textareaRef.value.style.height = 'auto';

  // Discuss mode (pre-grade, action row open): talk to the tutor, not the grader.
  if (discussMode.value) {
    if (!currentItem.value) return;
    const sid = await ensureChatSession();
    if (!sid) return;
    scrollToBottom();
    await sendEditChatMessage(sid, currentItem.value.id, text);
    scrollToBottom();
    return;
  }

  if (itemStatus.value === 'graded') {
    await handlePostGradeSubmit(text);
    return;
  }

  transcript.value.push({ role: 'learner', text });
  scrollToBottom();
  await gradeCurrent(text);
}

async function retry() {
  if (!lastSubmittedResponse.value) return;
  await gradeCurrent(lastSubmittedResponse.value);
}

// Override the grader's proposed rating before it's committed. Local-only — written
// to FSRS when the learner advances.
function setGrade(rating: Rating) {
  if (itemStatus.value !== 'graded' || committing.value) return;
  gradedRating.value = rating;
}

// Reveal the model answer instead of typing a response. Switches into self-grade
// mode; the flashcard flips to show the answer and the rating buttons appear.
function revealAnswer() {
  if (!canReveal.value) return;
  typedMode.value = false;
  itemStatus.value = 'revealed';
  scrollToBottom();
}

// Commit a self-graded reveal: no typed response, no LLM grader. Rates the card and
// advances immediately (classic flashcard).
async function selfGrade(rating: Rating) {
  const item = currentItem.value;
  if (!item || committing.value || itemStatus.value !== 'revealed') return;
  committing.value = true;
  errorMessage.value = '';
  try {
    await trpc.gradeFlashcard.mutate({ idOrShortId: item.id, rating });
    ratingTotals.value[rating] += 1;
    cardsReviewed.value += 1;
    currentIndex.value += 1;
    resetItemState();
    scrollToBottom();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to record grade';
    committing.value = false;
  }
}

async function advance() {
  if (committing.value) return;

  // A retired/rejected card drops out of the queue without committing a grade — even
  // if a grade was proposed before the learner archived it.
  if (isItemArchived.value) {
    currentIndex.value += 1;
    resetItemState();
    scrollToBottom();
    return;
  }

  // Commit the pending typed grade (if any) before moving on. Skipping an ungraded
  // card commits nothing.
  const item = currentItem.value;
  if (
    itemStatus.value === 'graded' &&
    gradedRating.value !== null &&
    aiRating.value !== null &&
    item !== null
  ) {
    committing.value = true;
    try {
      await trpc.commitCardGrade.mutate({
        idOrShortId: item.id,
        learnerResponse: lastSubmittedResponse.value,
        transcript: buildTranscriptString(),
        graderNotes: gradedNotes.value ?? undefined,
        rating: gradedRating.value,
        aiRating: aiRating.value,
      });
      ratingTotals.value[gradedRating.value] += 1;
      cardsReviewed.value += 1;
    } catch (e) {
      errorMessage.value = e instanceof Error ? e.message : 'Failed to record grade';
      committing.value = false;
      return;
    }
  }

  currentIndex.value += 1;
  resetItemState();
  scrollToBottom();
}

function goBack() {
  router.push('/learn');
}

// Watch the user's identity, not the session object — BetterAuth refetches the
// session on window focus, handing back a new `user` reference each time. Keying off
// `id` means a focus-refetch for the same user is a no-op, so switching tabs mid-review
// no longer reloads the queue and wipes the typed answer.
watch(
  () => user.value?.id,
  (id, prevId) => {
    if (id && id !== prevId) loadQueue();
  }
);

watch(
  () => chatMessages.value.map((m) => m.content).join(''),
  () => scrollToBottom()
);

watch(
  () => editChatMessages.value.map((m) => m.content).join(''),
  () => scrollToBottom()
);

// Merge edits from either chat thread (reflection or gradeless editor) into the
// displayed card so it updates live regardless of which chat made the edit. Apply
// whichever ref just changed so a later edit never loses to a stale one.
watch(
  [reflectionCardUpdate, editCardUpdate],
  ([reflection, edit], [prevReflection, prevEdit]) => {
    let update = null;
    if (edit && edit !== prevEdit) update = edit;
    else if (reflection && reflection !== prevReflection) update = reflection;
    if (!update) return;
    liveCardEdit.value = { question: update.question, answer: update.answer };
    if (currentItem.value) {
      queue.value[currentIndex.value] = {
        ...currentItem.value,
        question: update.question,
        answer: update.answer,
      };
    }
  }
);

watch([chatStreaming, advanceRequested], ([streaming, requested]) => {
  if (requested && !streaming) advance();
});

// Flashcard keyboard shortcuts: Space reveals the answer; once revealed, 1–4 self-rate.
// Ignored while typing in the input (so Space/digits land in the textarea).
function handleItemKeydown(e: KeyboardEvent) {
  if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;

  // Once the session is finished (or nothing was due), Space exits to the library —
  // mirrors the on-screen button so you can clear the queue without reaching for the mouse.
  if (isSessionComplete.value || isQueueEmpty.value) {
    if (e.key === ' ') {
      e.preventDefault();
      goBack();
    }
    return;
  }

  if (!flashcardMode.value) return;

  if (itemStatus.value === 'revealed') {
    if (e.key >= '1' && e.key <= '4') {
      e.preventDefault();
      void selfGrade(Number(e.key) as Rating);
    }
    return;
  }

  if (e.key === ' ') {
    e.preventDefault();
    revealAnswer();
  }
}

onMounted(() => {
  if (!sessionLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }
  if (user.value) loadQueue();
  window.addEventListener('keydown', handleItemKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleItemKeydown);
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 flex flex-col max-w-3xl w-full mx-auto px-4 py-6 min-h-0">
      <div class="mb-4 flex items-center justify-between gap-3">
        <button
          class="text-sm text-muted-foreground hover:text-foreground"
          @click="goBack"
        >
          {{ isGlobal ? '← Done' : '← Back to Curricula' }}
        </button>
        <div
          v-if="!dataLoading && !isQueueEmpty && !isSessionComplete"
          class="flex items-center gap-3"
        >
          <Button
            v-if="flashcardMode"
            variant="ghost"
            size="sm"
            class="text-muted-foreground"
            @click="toggleActions"
          >
            <MessageSquare class="size-4" />
            Discuss
          </Button>
          <span class="text-sm text-muted-foreground">
            Card {{ Math.min(currentIndex + 1, totalInSession) }} of {{ totalInSession }}
          </span>
          <Button
            v-if="itemStatus === 'graded'"
            size="sm"
            :disabled="committing"
            @click="advance"
          >
            {{ committing ? 'Saving…' : 'Next →' }}
          </Button>
          <Button
            v-else
            variant="ghost"
            size="sm"
            :disabled="itemStatus === 'submitting'"
            @click="advance"
          >
            Skip
          </Button>
        </div>
      </div>

      <div
        v-if="dataLoading || sessionLoading"
        class="flex-1 flex items-center justify-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="isQueueEmpty"
        class="flex-1 flex items-center justify-center"
      >
        <div class="bg-card rounded-lg border border-border p-8 text-center space-y-4 max-w-md">
          <div class="text-4xl">
            &#10003;
          </div>
          <h2 class="text-xl font-semibold">
            {{ isGlobal ? "You're all caught up" : 'Nothing due right now' }}
          </h2>
          <p class="text-muted-foreground text-sm">
            {{ isGlobal ? 'No cards are due anywhere right now. Check back later.' : 'There are no cards due in this curriculum. Check back later.' }}
          </p>
          <Button
            variant="outline"
            @click="goBack"
          >
            {{ isGlobal ? 'Go to library' : 'Back to Curricula' }}
            <span class="ml-2 text-xs opacity-60">Space</span>
          </Button>
        </div>
      </div>

      <div
        v-else-if="isSessionComplete"
        class="flex-1 flex items-center justify-center"
      >
        <div class="bg-card rounded-lg border border-border p-8 text-center space-y-4 max-w-md w-full">
          <div class="text-4xl">
            &#127881;
          </div>
          <h2 class="text-xl font-semibold">
            Session complete
          </h2>
          <p class="text-muted-foreground text-sm">
            {{ cardsReviewed }} {{ cardsReviewed === 1 ? 'card' : 'cards' }} reviewed
          </p>
          <div class="space-y-2 text-sm">
            <div
              v-for="rating in [1, 2, 3, 4] as Rating[]"
              :key="rating"
              class="flex items-center justify-between border-b border-border pb-1 last:border-0"
            >
              <span class="text-muted-foreground">
                {{ ratingMeta[rating].label }}
              </span>
              <span class="font-medium">
                {{ ratingTotals[rating] }}
              </span>
            </div>
          </div>
          <Button @click="goBack">
            {{ isGlobal ? 'Go to library' : 'Back to Curricula' }}
            <span class="ml-2 text-xs opacity-60">Space</span>
          </Button>
        </div>
      </div>

      <div
        v-else-if="currentItem"
        class="flex-1 flex flex-col min-h-0"
      >
        <!-- Flashcard view (default): deck context, prominent question, Show answer, self-grade. -->
        <template v-if="flashcardMode">
          <div class="flex-1 overflow-y-auto py-4 flex flex-col items-center justify-center">
            <div class="w-full max-w-xl mx-auto space-y-4">
              <!-- Which curriculum this card comes from — context for the interleaved queue. -->
              <div
                v-if="displayItem?.curriculumTitle"
                class="text-center text-xs uppercase tracking-wide text-muted-foreground truncate"
                :title="displayItem.curriculumTitle"
              >
                {{ displayItem.curriculumTitle }}
              </div>

              <div class="bg-card border border-border rounded-lg p-6 space-y-5">
                <div
                  v-if="displayItem?.goal"
                  class="text-xs uppercase tracking-wide text-muted-foreground"
                >
                  Goal: <span class="normal-case tracking-normal italic">{{ displayItem.goal }}</span>
                </div>
                <div
                  class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                  v-html="marked.parse(normalizeMarkdown(displayItem?.question ?? ''))"
                />
                <template v-if="itemStatus === 'revealed'">
                  <hr class="border-border">
                  <div class="text-xs uppercase tracking-wide text-muted-foreground">
                    Answer
                  </div>
                  <div
                    class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                    v-html="marked.parse(normalizeMarkdown(displayItem?.answer ?? ''))"
                  />
                </template>
              </div>

              <!-- Controls sit directly under the card: reveal, then self-grade. -->
              <template v-if="itemStatus !== 'revealed'">
                <div class="flex justify-center">
                  <Button @click="revealAnswer">
                    Show answer
                    <span class="ml-2 text-xs opacity-60">Space</span>
                  </Button>
                </div>
              </template>
              <template v-else>
                <div class="text-xs text-muted-foreground text-center">
                  How well did you know it?
                </div>
                <div class="grid grid-cols-4 gap-2">
                  <Button
                    v-for="rating in [1, 2, 3, 4] as Rating[]"
                    :key="rating"
                    :disabled="committing"
                    class="flex-col h-auto py-2 border-0 text-white transition-opacity opacity-90 hover:opacity-100"
                    :class="ratingMeta[rating].color"
                    @click="selfGrade(rating)"
                  >
                    <span>{{ ratingMeta[rating].label }}</span>
                    <span class="text-xs opacity-60">{{ rating }}</span>
                  </Button>
                </div>
              </template>
            </div>
          </div>

          <div
            v-if="errorMessage"
            class="bg-destructive/10 text-destructive p-3 rounded-md mb-3 text-sm"
          >
            {{ errorMessage }}
          </div>
        </template>

        <!-- Conversational view: typed answer, grading, reflection, discuss/edit. -->
        <template v-else>
          <div
            ref="messagesContainer"
            class="flex-1 overflow-y-auto space-y-3 pb-4"
          >
            <template
              v-for="(bubble, idx) in chatLog"
              :key="idx"
            >
              <div
                v-if="bubble.kind === 'learner' || (bubble.kind === 'chat' && bubble.role === 'user')"
                class="flex justify-end"
              >
                <div
                  class="max-w-[85%] rounded-lg px-4 py-3 whitespace-pre-wrap bg-primary text-primary-foreground"
                >
                  {{ bubble.kind === 'chat' ? bubble.content.trim() : bubble.text }}
                </div>
              </div>

              <div
                v-else-if="bubble.kind === 'prompt'"
                class="flex justify-start"
              >
                <div
                  class="max-w-[85%] rounded-lg px-4 py-3 bg-muted text-foreground"
                >
                  <div
                    v-if="bubble.goal"
                    class="text-xs uppercase tracking-wide text-muted-foreground mb-1.5"
                  >
                    Goal: <span class="normal-case tracking-normal italic">{{ bubble.goal }}</span>
                  </div>
                  <div
                    class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                    v-html="marked.parse(normalizeMarkdown(bubble.text))"
                  />
                </div>
              </div>

              <div
                v-else-if="bubble.kind === 'answer'"
                class="flex justify-start"
              >
                <div
                  class="max-w-[85%] rounded-lg px-4 py-3 bg-muted text-foreground"
                >
                  <div class="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
                    Answer
                  </div>
                  <div
                    class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                    v-html="marked.parse(normalizeMarkdown(bubble.text))"
                  />
                </div>
              </div>

              <div
                v-else-if="bubble.kind === 'thinking'"
                class="flex justify-start"
              >
                <div class="max-w-[85%] rounded-lg px-4 py-3 bg-muted text-foreground">
                  <span class="inline-flex gap-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
                  </span>
                </div>
              </div>

              <div
                v-else-if="bubble.kind === 'grade'"
                class="flex justify-start"
              >
                <div class="w-full max-w-[85%] rounded-lg px-4 py-3 bg-muted text-foreground space-y-3">
                  <div class="space-y-1.5">
                    <div class="text-xs text-muted-foreground">
                      Grade — tap to change, saved when you continue
                    </div>
                    <div class="grid grid-cols-4 gap-2">
                      <Button
                        v-for="rating in [1, 2, 3, 4] as Rating[]"
                        :key="rating"
                        :disabled="committing"
                        class="flex-col h-auto py-2 border-0 text-white transition-opacity"
                        :class="[
                          ratingMeta[rating].color,
                          bubble.rating === rating
                            ? 'opacity-100 ring-2 ring-foreground/70'
                            : 'opacity-45 hover:opacity-75',
                        ]"
                        @click="setGrade(rating)"
                      >
                        <span>{{ ratingMeta[rating].label }}</span>
                        <span class="text-[10px] uppercase tracking-wide opacity-80 h-3 leading-3">
                          {{ aiRating === rating ? 'AI' : '' }}
                        </span>
                      </Button>
                    </div>
                    <p
                      v-if="bubble.rating !== aiRating"
                      class="text-xs text-muted-foreground"
                    >
                      Overriding the grader's pick ({{ ratingMeta[aiRating ?? bubble.rating].label }}).
                    </p>
                  </div>
                  <details
                    v-if="bubble.notes"
                    class="text-sm group"
                  >
                    <summary class="cursor-pointer text-muted-foreground hover:text-foreground select-none list-none">
                      <span class="group-open:hidden">Show grader rationale</span>
                      <span class="hidden group-open:inline">Hide grader rationale</span>
                    </summary>
                    <div class="whitespace-pre-wrap pt-2 text-muted-foreground">
                      {{ bubble.notes }}
                    </div>
                  </details>
                </div>
              </div>

              <div
                v-else-if="bubble.kind === 'chat' && bubble.role === 'assistant'"
                class="flex justify-start"
              >
                <div class="max-w-[85%] rounded-lg px-4 py-3 bg-muted text-foreground">
                  <div
                    class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                    v-html="marked.parse(normalizeMarkdown(bubble.content.trim()))"
                  />
                  <span
                    v-if="bubble.isStreaming && !bubble.content"
                    class="inline-flex gap-1"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
                  </span>
                  <div
                    v-if="bubble.toolUses?.length"
                    class="mt-2 flex flex-wrap gap-1"
                  >
                    <div
                      v-for="tu in bubble.toolUses"
                      :key="tu.id"
                      class="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-border bg-background/60"
                      :class="tu.status === 'error' ? 'text-destructive' : 'text-muted-foreground'"
                    >
                      <span
                        v-if="tu.status === 'running'"
                        class="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
                      />
                      <span
                        v-else-if="tu.status === 'done'"
                        class="text-green-600 dark:text-green-400"
                      >&#10003;</span>
                      <span v-else>&#9888;</span>
                      {{ toolLabel(tu.name, tu.status) }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div
            v-if="errorMessage"
            class="bg-destructive/10 text-destructive p-3 rounded-md mb-3 text-sm flex items-center justify-between gap-3"
          >
            <span>{{ errorMessage }}</span>
            <Button
              v-if="itemStatus === 'error'"
              size="sm"
              variant="outline"
              @click="retry"
            >
              Retry
            </Button>
          </div>

          <div
            v-if="chatError || editChatError"
            class="bg-destructive/10 text-destructive p-2 rounded-md mb-3 text-xs"
          >
            {{ chatError || editChatError }}
          </div>

          <div class="border-t border-border pt-3 pb-1 space-y-2">
            <div
              v-if="actionsOpen && !isItemArchived"
              class="grid grid-cols-2 sm:grid-cols-4 gap-2"
            >
              <Button
                variant="outline"
                size="sm"
                :disabled="archiving"
                @click="archiveItem('retire')"
              >
                Retire
              </Button>
              <Button
                variant="outline"
                size="sm"
                :disabled="archiving"
                @click="archiveItem('reject')"
              >
                Reject
              </Button>
              <Button
                variant="outline"
                size="sm"
                :disabled="editChatStreaming || chatStreaming"
                @click="sendItemMessage('This card felt too easy. Could you make it more challenging?')"
              >
                Too easy
              </Button>
              <Button
                variant="outline"
                size="sm"
                :disabled="editChatStreaming || chatStreaming"
                @click="sendItemMessage('This card felt too hard. Could you make it easier or more approachable?')"
              >
                Too hard
              </Button>
            </div>

            <div
              v-if="itemStatus === 'revealed' && !discussMode"
              class="space-y-1.5"
            >
              <div class="text-xs text-muted-foreground">
                How well did you know it?
              </div>
              <div class="grid grid-cols-4 gap-2">
                <Button
                  v-for="rating in [1, 2, 3, 4] as Rating[]"
                  :key="rating"
                  :disabled="committing"
                  class="flex-col h-auto py-2 border-0 text-white transition-opacity opacity-90 hover:opacity-100"
                  :class="ratingMeta[rating].color"
                  @click="selfGrade(rating)"
                >
                  <span>{{ ratingMeta[rating].label }}</span>
                </Button>
              </div>
            </div>

            <form
              v-else
              class="flex items-end gap-2"
              @submit.prevent="handleSubmit"
            >
              <textarea
                ref="textareaRef"
                v-model="inputText"
                :disabled="inputDisabled"
                rows="1"
                class="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none max-h-40 overflow-y-auto"
                :placeholder="inputPlaceholder"
                @keydown.enter.exact.prevent="handleSubmit"
                @input="autoResize"
              />
              <Button
                type="submit"
                :disabled="inputDisabled || !inputText.trim()"
              >
                {{
                  itemStatus === 'submitting'
                    ? 'Grading...'
                    : discussMode
                      ? editChatStreaming
                        ? 'Sending...'
                        : 'Send'
                      : chatStreaming
                        ? 'Sending...'
                        : 'Send'
                }}
              </Button>
            </form>

            <div
              v-if="itemStatus !== 'submitting' && !isItemArchived"
              class="flex justify-center gap-2"
            >
              <Button
                v-if="canReveal"
                variant="ghost"
                size="sm"
                @click="revealAnswer"
              >
                Reveal answer
                <span class="ml-2 text-xs opacity-60">Space</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                @click="toggleActions"
              >
                {{ actionsOpen ? (itemStatus === 'graded' ? 'Hide options' : 'Back to answering') : 'Discuss / edit this card' }}
              </Button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </AppLayout>
</template>
